import axios from 'axios'
import * as loadingActions from 'Store/Loading/actions'
import * as errorActions from 'Store/Error/actions'
import * as c from 'shared/constants'
import { toast } from 'react-toastify'
import { clearAdvancedSearchAbortController } from 'Store/Request/advancedSearchAbort'

const ERROR_400_MESSAGE = 'Incorrect username or password.'
const ERROR_401_MESSAGE = 'Please login for access. You will need approval to view protected data.'
const ERROR_403_MESSAGE = 'You do not have permission to perform this action. Request access via the "My Dashboard" tab to protected data.'
const ERROR_404_MESSAGE = 'Not found.'
const ERROR_500_MESSAGE = 'Oops, something went wrong.'
const ERROR_502_MESSAGE = 'The app is temporarily down for maintenance.'
const ERROR_408_MESSAGE =
  'Your search timed out after two minutes. Try again, narrow your filters, or use Cancel search while a query is running.'

export const handleActionDispatch = (dispatch, constant, requestId) => {
  dispatch(loadingActions.handleRequest(constant, requestId))
  dispatch(errorActions.handleClearErrors(constant))
}

const findErrorKeyValue = (status, data) => {
  if (!data) return ERROR_500_MESSAGE
  try {
    switch (status) {
      case 400:
        if (data['email'] && data['email'].includes('user request with this email already exists.')) {
          return `We already have an account request for this email. Please email ${c.CONTACT_EMAIL
            } if you need to check your status.`
        } else if (data['errors'] && data['errors'].length) {
          return data['errors'].map(error => `${error}`)
        } else {
          return ERROR_400_MESSAGE
        }

      case 401:
        return ERROR_401_MESSAGE
      case 403:
        return ERROR_403_MESSAGE
      case 404:
        return ERROR_404_MESSAGE
      case 500:
        return ERROR_500_MESSAGE
      case 422:
        return typeof data === 'string' ? data : (data.detail || data.non_field_errors || ERROR_500_MESSAGE)
      case 502:
        return ERROR_502_MESSAGE
      default:
        return data.detail || data.non_field_errors || data.refresh || (typeof data === 'string' ? data : ERROR_500_MESSAGE)
    }
  } catch (err) {
    return ERROR_500_MESSAGE
  }
}

export const handleCatchError = (error, type, dispatch, requestId) => {
  if (axios.isCancel(error)) {
    clearAdvancedSearchAbortController()
    dispatch(loadingActions.handleCompletedRequest(type, requestId))
    return
  }

  let errorMessage = ''
  let errorStatus = ''
  if (!error.response) {
    if (error.status === 504 || (error.message && error.message.toUpperCase().match(/(TIME)/))) {
      errorMessage = ERROR_408_MESSAGE
      errorStatus = 408
    } else if (error.status === 504) {
      errorMessage = ERROR_500_MESSAGE
      errorStatus = error.status
    } else {
      errorMessage = findErrorKeyValue(error.status, error)
      errorStatus = 500
    }
  } else {
    errorStatus = error.response.status
    if (error.response.status === 500) {
      errorMessage = error.response.data.results || error.response.data.detail
    } else if (error.response.status > 200) {
      errorMessage = findErrorKeyValue(error.response.status, error.response.data)
    }
  }

  // Always dispatch the failure so UI components (e.g. LookupTableTab) can
  // detect 401/403 via props.error.status and render the appropriate auth-gating
  // CTA (login / request access). Without this, sensitive-data tabs like
  // Foreclosures and OCA Housing Court silently showed "no results" instead of
  // the "Login to view" button.
  if (type === c.ADVANCED_SEARCH) {
    clearAdvancedSearchAbortController()
  }

  dispatch(errorActions.handleFailure(type, errorStatus, errorMessage))

  // Suppress only the toast on 401 — user is simply not logged in, no need to
  // spam them. Error state is still set above so the inline auth CTAs work.
  if (errorStatus !== 401) {
    handleToast(errorStatus, errorMessage)
  }

  dispatch(loadingActions.handleCompletedRequest(type, requestId))
}

export const handleToast = (errorStatus, errorMessage) => {
  switch (errorStatus) {
    case 502:
      toast.error(errorMessage)
      break
    default:
      return
  }
}

export const constructActionKey = constants => {
  return constants.filter(c => c).join('_')
}

export const constructSimplePropertyParams = params => {
  // Takes an objects like:
  // { type: 'hpdviolations', comparison: 'gte', value: 10, startDate: '2017-01-01', endDate: '2018-01-01'}
  // And converts it to url param object:
  // { hpdviolations__gte: 10, hpdviolations__start: '2017-01-01', hpdviolations__end: '2018-01-01'}
  return {
    [`${params.type}__${params.comparison}`]: params.value,
    [`${params.type}__start`]: params.startDate,
    [`${params.type}__end`]: params.endDate,
  }
}
